import * as resourceService from './resource';
import * as arrayUtil from './arrayUtils';

type Occupancy = Core.Occupancy;
type OccupantRange = Core.OccupantRange;

const minAdults: number = TRPCONFIG.Domain.MinimumAdultOccupancy || 2;
const minParkingPassengerCount: number = 1;
let occupancy: Occupancy[] = [{
    Adults: minAdults,
    ChildAges: []
}];
let passengerCount: number = minParkingPassengerCount;
let callback: (occ:Occupancy[]) => void;

export const ages = arrayUtil.numberArray(0, 17);

export function getNumberOfNonInfantsForRoom(index: number): number {
    return getNumberOfNonInfantPassengers(occupancy[index]);
}

export function addRoom(): Occupancy[] {
    occupancy.push({Adults: minAdults, ChildAges: []});
    return occupancy;
}

export function removeRoom(index: number): Occupancy[] {
    occupancy.splice(index, 1);
    return occupancy;
}

export function getOccupancy(): Occupancy[] {
    return occupancy;
}

export function setOccupancy(o: Occupancy[]): Occupancy[] {
    occupancy = o.map(function(r: Occupancy|undefined|null) {
        return _.assign<any, Occupancy, any, Occupancy>({}, {
            ChildAges: [],
            Adults: minAdults
        }, r);
    });
    return o;
}

export function enforceAreaLimits(area: string): void {
    const limits = getLimits(area);
    if (limits == null) {
        return;
    }

    occupancy.forEach(function(o) {
        o.Adults = o.Adults > limits.maxAdults ?
            limits.maxAdults :
            o.Adults;

        if (Array.isArray(o.ChildAges) && o.ChildAges.length > limits.maxChildren) {
            o.ChildAges.splice(limits.maxChildren);
        }
    });
}

export function adultRange(area: string): OccupantRange {
    return {
        min: 1,
        max: getLimits(area).maxAdults
    };
}

export function childRange(area: string): OccupantRange {
    return {
        min: 0,
        max: getLimits(area).maxChildren
    };
}

//this will take the array of occupancies and reduce them down to a single one
//useful for the cases where we don't care about the split into rooms.
export function reduceOccupancy(occ?: Occupancy[]): Occupancy {
    return arrayUtil.reduce<Occupancy, Occupancy>(function(o1, o2) {
        return {
            Adults: o1.Adults + o2.Adults,
            ChildAges: (o1.ChildAges||[]).concat(o2.ChildAges||[])
        };
    }, occ || _.cloneDeep<Occupancy[]>(getOccupancy()));
}

export function getNumberOfNonInfantPassengers(occ?: Occupancy): number {
    function nonInfants(occupancy: Occupancy): number {
        return (occupancy.ChildAges||[]).filter(a => a >= 2).length;
    }

    const $location = angular.element(document).injector().get('$location');
    if ($location.search().occ) {
        const o = parseOccupancyString($location.search().occ);
        if (!o) { return 0; }
        const occupancy = _.first<Occupancy>(o);
        return _.get<number>(occupancy, 'Adults') + nonInfants(occupancy);
    }

    const o = occ || reduceOccupancy();
    return o.Adults + nonInfants(o);
}

export function moreInfantsThanAdults(): boolean {
    const o = reduceOccupancy();

    function countInfants(value) {
        return value < 2;
    }

    return o.Adults < arrayUtil.count(o.ChildAges||[], countInfants);
}

export function childAgesPopulated(occupancy: Occupancy[]): boolean {
    occupancy = occupancy || getOccupancy();
    for (let r = occupancy.length - 1; r >= 0; r--) {
        const ages = occupancy[r].ChildAges || [];
        for (let ca = ages.length - 1; ca >= 0; ca--) {
            if (ages[ca] == null) {
                return false;
            }
        }
    }
    return true;
}

export function applyOccupancy(): void {
    if (callback) {
        callback(getOccupancy());
    }
}

export function onApplyOccupancy(fn: (occ:Occupancy[]) => void): void {
    callback = fn;
}

export function getOccupancyStringForRoom(index: number, occ: Occupancy[]): string {
    const str = `${resourceService.get('ROOM')} ${index + 1}`;
    return `${str} (${getOccupancyString([occ ? occ[index] : occupancy[index]])})`;
}

export function getRoomOccupancyString(occ: Occupancy[]): string {
    occ = (occ || occupancy);

    let str = occupancyProposition(
        occ.length,
        resourceService.get('ROOM'),
        resourceService.get('ROOMS')
    );

    str += ` (${getOccupancyString(occ)})`;
    return str;
}

export function getOccupancyString(occ: Occupancy[]): string {
    let str;
    const counts = getTotalPeopleCounts(occ);

    str = occupancyProposition(
        counts.adults,
        resourceService.get('ADULT'),
        resourceService.get('ADULTS')
    );

    if (counts.children) {
        str += ', ';
        str += occupancyProposition(
            counts.children,
            resourceService.get('CHILD'),
            resourceService.get('CHILDREN')
        );
    }

    return str;
}

export function getTotalPeopleCounts(occ: Occupancy[]): {adults: number, children: number} {
    const result = {
        adults: 0,
        children: 0
    };
    (occ || occupancy).forEach(o => {
        result.adults += o.Adults;
        result.children += _.get(o, 'ChildAges.length', 0);
    });
    return result;
}

export function parseOccupancyString(o: string|undefined|null): Occupancy[]|undefined {
    if (!o) { return; }
    const rooms = _(o.toLowerCase()).split('a').tail().value();
    return _.map(rooms, r => {
        const [ adults, ...childAges ] = _.split(r.toLowerCase(), 'c');
        return {
            Adults: parseInt(adults, 10),
            ChildAges: _.map(childAges, v => parseInt(v, 10))
        };
    });
}

export function setPassengerCount(n: number): void {
    // Passenger count where age is not a factor i.e. airport parking
    // Not currently synced with `occupancy`
    passengerCount = n || minParkingPassengerCount;
}

export function getPassengerCount(): number {
    return passengerCount;
}

export function serialise(occ: Occupancy[] = []): string {
    return _.reduce(occ, (serialized, o) => {
        serialized += `A${o.Adults || 0}`;
        _.each(o.ChildAges||[], ca => serialized += `C${ca || 0}`);
        return serialized;
    }, '');
}

export function deserialise(o: string): {adults:number, children:number, infants:number} {
    const raw = /A(\d*)C(\d*)I(\d)$/.exec(o);
    if (raw) {
        return {
            adults: parseInt(raw[1] || '0', 10),
            children: parseInt(raw[2] || '0', 10),
            infants: parseInt(raw[3] || '0', 10)
        };
    }
    return {
        adults: 2,
        children: 0,
        infants: 0
    };
}

export function isRoomKidsFree(index: number): boolean {
    const o = _.get<number[]>(occupancy, `[${index}].ChildAges`);
    const num = _.size(o);
    return num > 0 && num <= 3;
}

export function getOnlyAdultsPerRoom(i: number): number {
    return _.get<number>(occupancy, `[${i}].Adults`) || 0;
}

export function getOnlyPayingIndividuals(): number {
    return _.reduce(occupancy, (sum, room) => {
        const numChildren = _.filter(room.ChildAges || [], ca => ca > 2).length;
        const numAdults = _.get<number>(room, 'Adults') || 0;
        if (numChildren <= 3) {
            return sum + numAdults;
        }
        return sum + numChildren + numAdults;
    }, 0);
}

function getLimits(area: string) {
    return TRPCONFIG.Domain.OccupancyLimits[area];
}

function occupancyProposition(number: number, singular: string, plural: string): string {
    return `${number} ${number > 1 ? plural : singular}`;
}
